/**
 * Passcode gate for the wholesale rate card.
 *
 * WHAT THIS DOES: stops a customer who stumbles onto /trade-rates from seeing
 * trade pricing. That was the actual worry, and this solves it.
 *
 * WHAT THIS DOES NOT DO: make the rates secret. This is a static site — the
 * check runs in the visitor's own browser, so anyone who knows to look can read
 * the rates out of the JavaScript bundle without ever meeting this screen. The
 * passcode is stored as a SHA-256 hash rather than plain text, which keeps it
 * from being readable at a glance, but a determined person is not stopped by
 * client-side code of any kind.
 *
 * If the rates ever need to be genuinely private, they have to move out of the
 * bundle and into Firestore behind security rules, so an unauthorised browser
 * never receives them at all.
 *
 * TO CHANGE THE PASSCODE: node scripts/set-trade-passcode.js "your new passcode"
 */
const PASSCODE_SHA256 =
  "c1e9fee9eb125016549f2ab0a26f6c86e1e40661c47294b16a33648cfb3776ab";

/** Cleared when the tab closes, so a shared or public device doesn't stay open. */
const SESSION_KEY = "nt-trade-rates-unlocked";

async function sha256Hex(text) {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function verifyPasscode(input) {
  // crypto.subtle needs a secure context: https, or localhost in development.
  if (!window.crypto?.subtle) return false;
  try {
    const hashed = await sha256Hex(String(input).trim());
    return hashed === PASSCODE_SHA256;
  } catch {
    return false;
  }
}

export function isUnlocked() {
  try {
    return sessionStorage.getItem(SESSION_KEY) === "1";
  } catch {
    // Private browsing and locked-down settings can throw on access.
    return false;
  }
}

export function rememberUnlock() {
  try {
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    /* Not fatal — the page just asks again on the next navigation. */
  }
}

export function forgetUnlock() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* no-op */
  }
}
